import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RESOURCE_TYPES, INC_SEVERITY } from "@/lib/fleetConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FieldSelect } from "@/components/ui/field-select";
import { ReceiptField } from "@/components/finance/ReceiptField";
import { Loader2, AlertTriangle } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const label = "mb-1 block text-sm font-medium text-slate-700";

// Report incident dialog. resources: [{id, internal_reference, name}], fixedResourceId optional, bookingId optional.
export function IncidentDialog({ open, onOpenChange, resources = [], fixedResourceId, bookingId }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [resourceId, setResourceId] = useState(fixedResourceId || "");
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState("MEDIUM");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState(false);
  const [receipt, setReceipt] = useState(null);
  React.useEffect(() => { if (fixedResourceId) setResourceId(fixedResourceId); }, [fixedResourceId, open]);

  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/incidents", {
      resource_id: resourceId, title, severity, description: description || null,
      blocks_resource: blocks, booking_id: bookingId || null, receipt_file_id: receipt?.id || null,
    }, { params: { organization_id: orgId } })).data,
    onSuccess: (d) => { toast.success(`Incident ${d.incident_reference} signalé`); qc.invalidateQueries(); onOpenChange(false); setTitle(""); setDescription(""); setBlocks(false); setReceipt(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="incident-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Signaler un incident</DialogTitle>
          <DialogDescription className="sr-only">Signalez un incident sur une ressource.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!fixedResourceId && (
            <div><label className={label}>Ressource</label>
              <FieldSelect testId="incident-resource-select" className="w-full" value={resourceId} onChange={setResourceId}
                placeholder="Choisir une ressource" options={resources.map((r) => ({ value: r.id, label: `${r.internal_reference} — ${r.name}` }))} />
            </div>
          )}
          <div><label className={label}>Titre</label><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="incident-title-input" className={input} placeholder="Ex: Frein arrière défectueux" /></div>
          <div><label className={label}>Gravité</label>
            <FieldSelect testId="incident-severity-select" className="w-full" value={severity} onChange={setSeverity}
              options={Object.entries(INC_SEVERITY).map(([k, v]) => ({ value: k, label: v.label }))} />
          </div>
          <div><label className={label}>Description</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="incident-description-input" className={input} /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={blocks} onChange={(e) => setBlocks(e.target.checked)} data-testid="incident-blocks-checkbox" className="h-4 w-4 rounded border-slate-300" /> Bloque la ressource (met hors service)</label>
          <div><label className={label}>Justificatif (optionnel)</label><ReceiptField value={receipt} onChange={setReceipt} /></div>
          <button disabled={mut.isPending || !resourceId || !title.trim()} onClick={() => mut.mutate()} data-testid="submit-incident-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Signaler
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ResourceDialog({ open, onOpenChange }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState({ resource_type: "QUAD" });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/fleet", {
      name: f.name, resource_type: f.resource_type, internal_reference: f.internal_reference || null,
      brand: f.brand || null, model: f.model || null, year: f.year ? Number(f.year) : null,
      registration_number: f.registration_number || null, notes: f.notes || null,
    }, { params: { organization_id: orgId } })).data,
    onSuccess: (d) => { toast.success(`Ressource ${d.internal_reference} créée`); qc.invalidateQueries(); onOpenChange(false); setF({ resource_type: "QUAD" }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="resource-dialog">
        <DialogHeader><DialogTitle>Nouvelle ressource</DialogTitle><DialogDescription className="sr-only">Ajouter une ressource à la flotte.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className={label}>Nom</label><input value={f.name || ""} onChange={set("name")} data-testid="resource-name-input" className={input} placeholder="Yamaha Grizzly 700" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Type</label><FieldSelect testId="resource-type-select" className="w-full" value={f.resource_type} onChange={(v) => setF({ ...f, resource_type: v })} options={RESOURCE_TYPES.map((r) => ({ value: r, label: r }))} /></div>
            <div><label className={label}>Référence (auto si vide)</label><input value={f.internal_reference || ""} onChange={set("internal_reference")} data-testid="resource-ref-input" className={input} placeholder="QUAD-009" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Marque</label><input value={f.brand || ""} onChange={set("brand")} data-testid="resource-brand-input" className={input} /></div>
            <div><label className={label}>Modèle</label><input value={f.model || ""} onChange={set("model")} className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Année</label><input type="number" value={f.year || ""} onChange={set("year")} className={input} /></div>
            <div><label className={label}>Immatriculation</label><input value={f.registration_number || ""} onChange={set("registration_number")} className={input} /></div>
          </div>
          <div><label className={label}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} className={input} /></div>
          <button disabled={mut.isPending || !f.name?.trim()} onClick={() => mut.mutate()} data-testid="save-resource-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function ResourceEditDialog({ open, onOpenChange, resource }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState({});
  React.useEffect(() => { if (resource) setF({ ...resource }); }, [resource, open]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/api/fleet/${resource.id}`, {
      name: f.name, resource_type: f.resource_type, internal_reference: f.internal_reference || null,
      brand: f.brand || null, model: f.model || null, year: f.year ? Number(f.year) : null,
      registration_number: f.registration_number || null, serial_number: f.serial_number || null,
      notes: f.notes || null,
    }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Ressource mise à jour"); qc.invalidateQueries(); onOpenChange(false); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (!resource) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="resource-edit-dialog">
        <DialogHeader><DialogTitle>Modifier la ressource</DialogTitle><DialogDescription className="sr-only">Modifier les informations de la ressource.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className={label}>Nom</label><input value={f.name || ""} onChange={set("name")} data-testid="resource-edit-name-input" className={input} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Type</label><FieldSelect testId="resource-edit-type-select" className="w-full" value={f.resource_type || "QUAD"} onChange={(v) => setF({ ...f, resource_type: v })} options={RESOURCE_TYPES.map((r) => ({ value: r, label: r }))} /></div>
            <div><label className={label}>Référence</label><input value={f.internal_reference || ""} onChange={set("internal_reference")} data-testid="resource-edit-ref-input" className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Marque</label><input value={f.brand || ""} onChange={set("brand")} className={input} /></div>
            <div><label className={label}>Modèle</label><input value={f.model || ""} onChange={set("model")} className={input} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Année</label><input type="number" value={f.year || ""} onChange={set("year")} className={input} /></div>
            <div><label className={label}>Immatriculation</label><input value={f.registration_number || ""} onChange={set("registration_number")} className={input} /></div>
          </div>
          <div><label className={label}>Notes</label><textarea value={f.notes || ""} onChange={set("notes")} rows={2} className={input} /></div>
          <button disabled={mut.isPending || !f.name?.trim()} onClick={() => mut.mutate()} data-testid="save-resource-edit-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ArchiveResourceDialog({ open, onOpenChange, resource, onArchived }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [warn, setWarn] = useState(null);
  const mut = useMutation({
    mutationFn: async (confirm) => (await api.post(`/api/fleet/${resource.id}/archive`, { confirm }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Ressource archivée"); qc.invalidateQueries(); onOpenChange(false); setWarn(null); onArchived && onArchived(); },
    onError: (e) => { const d = e.response?.data?.detail; if (d && d.type === "future_bookings") setWarn(d); else toast.error(formatApiError(d) || e.message); },
  });
  if (!resource) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setWarn(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="archive-resource-dialog">
        <DialogHeader><DialogTitle>Archiver la ressource</DialogTitle><DialogDescription className="sr-only">Archiver définitivement la ressource sans perdre l'historique.</DialogDescription></DialogHeader>
        {!warn ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">La ressource <strong>{resource.internal_reference || resource.name}</strong> sera archivée. Son historique (réservations, maintenances, incidents) est conservé, mais elle ne pourra plus être affectée à de nouvelles réservations.</p>
            <button onClick={() => mut.mutate(false)} disabled={mut.isPending} data-testid="confirm-archive-button" className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">{mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Archiver</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="archive-warning">
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{warn.message}<ul className="mt-2 space-y-1">{warn.bookings.map((b) => <li key={b.id} className="font-mono text-xs">{b.booking_reference} · {b.booking_date} {b.start_time}</li>)}</ul></div>
            <p className="text-xs text-slate-500">Les réservations ne sont pas supprimées ; elles seront marquées « à réaffecter ».</p>
            <button onClick={() => mut.mutate(true)} disabled={mut.isPending} data-testid="confirm-archive-force-button" className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">{mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Archiver quand même</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
